import { useEffect, useState, useCallback } from 'react';

type ConsentMode = {
  functionality_storage?: string;
  security_storage?: string;
  ad_storage?: string;
  ad_user_data?: string;
  ad_personalization?: string;
  analytics_storage?: string;
  personalization_storage?: string;
  [key: string]: any;
};

type SimpleConsent = {
  necessary: boolean;
  analytics: boolean;
  preferences: boolean;
  marketing: boolean;
};

type ConsentState = {
  consent: SimpleConsent;
  consentMode: ConsentMode | null;
  hasConsented?: boolean;
  gpcApplied?: boolean;
  migratedFromOneTrust?: boolean;
  impliedConsent?: boolean;
};

type UIState = {
  showBanner: boolean;
  checkboxes: { analytics: boolean; preferences: boolean; marketing: boolean } | null;
  disabled: { analytics?: boolean; preferences?: boolean; marketing?: boolean } | null;
};

const CONSENT_COOKIE_DOMAIN = '.brightspeed.com';
const CONSENT_COOKIE_NAME = 'consentMode';
const CONSENT_COOKIE_DAYS = 365;
const CONSENT_MODEL = 'implied';

const GPC_CONFIG = {
  enabled: true,
  denyScope: 'all_non_essential',
  allowAnalytics: false,
  showBanner: false,
  allowOverride: true,
  recheckOnVisit: true,
};

function detectGPC() {
  if (!GPC_CONFIG.enabled) return false;
  try {
    // @ts-ignore
    return (typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true) || false;
  } catch (e) {
    return false;
  }
}

const GPC_DETECTED = typeof window !== 'undefined' ? detectGPC() : false;

function buildGPCConsentState(): ConsentMode {
  const consent: ConsentMode = {
    functionality_storage: 'granted',
    security_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: GPC_CONFIG.allowAnalytics ? 'granted' : 'denied',
    personalization_storage: 'granted',
  };

  if (GPC_CONFIG.denyScope === 'marketing_and_preferences') {
    consent.personalization_storage = 'denied';
  } else if (GPC_CONFIG.denyScope === 'all_non_essential') {
    consent.analytics_storage = 'denied';
    consent.personalization_storage = 'denied';
  }

  return consent;
}

function setConsentCookie(value: ConsentMode) {
  const expires = new Date();
  expires.setTime(expires.getTime() + CONSENT_COOKIE_DAYS * 24 * 60 * 60 * 1000);
  document.cookie =
    CONSENT_COOKIE_NAME +
    '=' +
    encodeURIComponent(JSON.stringify(value)) +
    '; expires=' +
    expires.toUTCString() +
    '; path=/' +
    '; domain=' +
    CONSENT_COOKIE_DOMAIN +
    '; SameSite=Lax' +
    '; Secure';
}

function getConsentCookie(): ConsentMode | null {
  const name = CONSENT_COOKIE_NAME + '=';
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.indexOf(name) === 0) {
      try {
        return JSON.parse(decodeURIComponent(cookie.substring(name.length)));
      } catch (e) {
        return null;
      }
    }
  }
  return null;
}

function getOneTrustCookie(): string | null {
  const name = 'OptanonConsent=';
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length);
    }
  }
  return null;
}

function parseOneTrustGroups(cookieValue: string | null): Record<string, boolean> | null {
  if (!cookieValue) return null;
  try {
    const decoded = decodeURIComponent(cookieValue);
    const groupsMatch = decoded.match(/groups=([^&]+)/);
    if (!groupsMatch) return null;

    const groupsString = groupsMatch[1];
    const groups: Record<string, boolean> = {};

    const pairs = groupsString.split(',');
    for (let i = 0; i < pairs.length; i++) {
      const parts = pairs[i].split(':');
      if (parts.length === 2) {
        const groupId = parts[0].trim();
        const consent = parts[1].trim() === '1';
        groups[groupId] = consent;
      }
    }

    return groups;
  } catch (e) {
    return null;
  }
}

function migrateOneTrustConsent(): ConsentMode | null {
  const oneTrustCookie = getOneTrustCookie();
  if (!oneTrustCookie) return null;

  const groups = parseOneTrustGroups(oneTrustCookie);
  if (!groups) return null;

  const consentMode: ConsentMode = {
    security_storage: groups['1'] ? 'granted' : 'denied',
    analytics_storage: groups['2'] ? 'granted' : 'denied',
    personalization_storage: groups['2'] ? 'granted' : 'denied',
    functionality_storage: groups['3'] ? 'granted' : 'denied',
    ad_storage: groups['4'] ? 'granted' : 'denied',
    ad_user_data: groups['4'] ? 'granted' : 'denied',
    ad_personalization: groups['4'] ? 'granted' : 'denied',
  };

  return consentMode;
}

function getConsentState(): ConsentState {
  const saved = getConsentCookie();

  if (saved) {
    return {
      consent: {
        necessary: true,
        analytics: saved.analytics_storage === 'granted',
        preferences: saved.personalization_storage === 'granted',
        marketing: saved.ad_storage === 'granted',
      },
      consentMode: saved,
      hasConsented: true,
    };
  }

  if (GPC_DETECTED) {
    const gpcState = buildGPCConsentState();
    return {
      consent: {
        necessary: true,
        analytics: gpcState.analytics_storage === 'granted',
        preferences: gpcState.personalization_storage === 'granted',
        marketing: gpcState.ad_storage === 'granted',
      },
      consentMode: gpcState,
      hasConsented: false,
      gpcApplied: true,
    };
  }

  const migratedConsent = migrateOneTrustConsent();
  if (migratedConsent) {
    return {
      consent: {
        necessary: true,
        analytics: migratedConsent.analytics_storage === 'granted',
        preferences: migratedConsent.personalization_storage === 'granted',
        marketing: migratedConsent.ad_storage === 'granted',
      },
      consentMode: migratedConsent,
      hasConsented: true,
      migratedFromOneTrust: true,
    };
  }

  if (CONSENT_MODEL === 'implied') {
    const impliedState: ConsentMode = {
      functionality_storage: 'granted',
      security_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'granted',
      personalization_storage: 'denied',
    };
    return {
      consent: {
        necessary: true,
        analytics: true,
        preferences: false,
        marketing: false,
      },
      consentMode: impliedState,
      hasConsented: false,
      impliedConsent: true,
    };
  }

  return {
    consent: {
      necessary: true,
      analytics: false,
      preferences: false,
      marketing: false,
    },
    consentMode: null,
    hasConsented: false,
  };
}

export default function useNewOptimizelyConsent() {
  const [consentState, setConsentState] = useState<ConsentState | null>(null);
  const [uiState, setUiState] = useState<UIState | null>(null);

  const isGPCEnabled = useCallback(() => GPC_DETECTED, []);
  const getGPCConfig = useCallback(() => GPC_CONFIG, []);

  // Expose for Optimizely init + legacy index.html consent handlers
  if (typeof window !== 'undefined') {
    // @ts-ignore
    window.getConsentState = getConsentState;
    // @ts-ignore
    window.getConsentCookie = getConsentCookie;
    // @ts-ignore
    window.setConsentCookie = setConsentCookie;
  }

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // ensure dataLayer exists
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.getConsentState = getConsentState;
    // @ts-ignore
    window.getConsentCookie = getConsentCookie;
    // @ts-ignore
    window.setConsentCookie = setConsentCookie;

    (function () {
      const localUiState: UIState = {
        showBanner: false,
        checkboxes: null,
        disabled: null,
      };

      const existingCookie = getConsentCookie();

      const shouldCheckGPC = GPC_DETECTED && (existingCookie === null || GPC_CONFIG.recheckOnVisit);

      if (shouldCheckGPC) {
        const gpcConsent = buildGPCConsentState();
        const gpcSimple = {
          necessary: true,
          analytics: gpcConsent.analytics_storage === 'granted',
          preferences: gpcConsent.personalization_storage === 'granted',
          marketing: false,
        };

        setConsentCookie(gpcConsent);

        // @ts-ignore
        window.dataLayer.push({
          event: 'consent_update',
          consent_state: gpcConsent,
          gpc_detected: true,
        });

        window.dispatchEvent(
          new CustomEvent('consentUpdated', {
            detail: {
              consent: gpcSimple,
              consentMode: gpcConsent,
              gpcDetected: true,
            },
          })
        );

        localUiState.checkboxes = {
          analytics: gpcSimple.analytics,
          preferences: gpcSimple.preferences,
          marketing: false,
        };

        if (!GPC_CONFIG.allowOverride) {
          localUiState.disabled = {
            marketing: true,
            preferences: !gpcSimple.preferences,
            analytics: !gpcSimple.analytics,
          };
        }

        localUiState.showBanner = GPC_CONFIG.showBanner;
      } else if (existingCookie === null) {
        const migratedConsent = migrateOneTrustConsent();
        if (migratedConsent) {
          setConsentCookie(migratedConsent);

          // @ts-ignore
          window.dataLayer.push({
            event: 'consent_migration',
            migration_source: 'onetrust',
            migrated_analytics: migratedConsent.analytics_storage,
            migrated_marketing: migratedConsent.ad_storage,
            migrated_preferences: migratedConsent.personalization_storage,
            migrated_functionality: migratedConsent.functionality_storage,
          });

          // @ts-ignore
          window.dataLayer.push({
            event: 'consent_update',
            consent_state: migratedConsent,
            migrated_from: 'onetrust',
          });

          window.dispatchEvent(
            new CustomEvent('consentUpdated', {
              detail: {
                consent: {
                  necessary: true,
                  analytics: migratedConsent.analytics_storage === 'granted',
                  preferences: migratedConsent.personalization_storage === 'granted',
                  marketing: migratedConsent.ad_storage === 'granted',
                },
                consentMode: migratedConsent,
                migratedFrom: 'onetrust',
              },
            })
          );

          localUiState.checkboxes = {
            analytics: migratedConsent.analytics_storage === 'granted',
            preferences: migratedConsent.personalization_storage === 'granted',
            marketing: migratedConsent.ad_storage === 'granted',
          };
        } else if (CONSENT_MODEL === 'implied') {
          const impliedConsent: ConsentMode = {
            functionality_storage: 'granted',
            security_storage: 'granted',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'granted',
            personalization_storage: 'denied',
          };

          setConsentCookie(impliedConsent);

          // @ts-ignore
          window.dataLayer.push({
            event: 'consent_update',
            consent_state: impliedConsent,
          });

          window.dispatchEvent(
            new CustomEvent('consentUpdated', {
              detail: {
                consent: {
                  necessary: true,
                  analytics: true,
                  preferences: false,
                  marketing: false,
                },
                consentMode: impliedConsent,
                implicit: true,
              },
            })
          );

          localUiState.showBanner = true;
        } else {
          localUiState.showBanner = true;
        }
      } else {
        // existing cookie present
        // @ts-ignore
        window.dataLayer.push({
          event: 'consent_update',
          consent_state: existingCookie,
        });

        window.dispatchEvent(
          new CustomEvent('consentUpdated', {
            detail: {
              consent: {
                necessary: true,
                analytics: existingCookie.analytics_storage === 'granted',
                preferences: existingCookie.personalization_storage === 'granted',
                marketing: existingCookie.ad_storage === 'granted',
              },
              consentMode: existingCookie,
              hasConsented: true,
            },
          })
        );
      }

      // Save states to React state and global var for compatibility
      // @ts-ignore
      window.__consentBannerUIState = localUiState;
      setUiState(localUiState);
      setConsentState(getConsentState());
    })();
  }, []);

  return {
    consentState,
    uiState,
    isGPCEnabled,
    getGPCConfig,
    getConsentCookie: typeof window !== 'undefined' ? getConsentCookie : () => null,
    setConsentCookie: typeof window !== 'undefined' ? setConsentCookie : (_: ConsentMode) => {},
    getConsentState: typeof window !== 'undefined' ? getConsentState : () => ({
      consent: { necessary: true, analytics: false, preferences: false, marketing: false },
      consentMode: null,
      hasConsented: false,
    } as ConsentState),
  };
}
